import React, { useState, useEffect } from 'react';

function TimerBar({
  timeLimitMinutes,
  startedAt,
  pausedAt,
  pausedSeconds,
  status,
  onSubmit,
  onAbandon,
  onTimeExpired,
  isReadOnly,
  isTeamBased
}) {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [hasTriggeredExpiry, setHasTriggeredExpiry] = useState(false);

  // Quan l'instructor desbloqueja un exercici de temps esgotat, el component no es
  // torna a muntar i hasTriggeredExpiry es quedaria a true per sempre: el compte
  // enrere arribaria a zero una segona vegada sense avisar el servidor. Es reinicia
  // amb el rellotge nou.
  useEffect(() => {
    if (status === 'in_progress') {
      setHasTriggeredExpiry(false);
    }
  }, [status, startedAt, pausedSeconds]);

  useEffect(() => {
    if (!timeLimitMinutes || !startedAt) return;
    if (status === 'paused') return;
    if (['submitted', 'time_expired', 'abandoned', 'graded'].includes(status)) {
      setTimeRemaining(0);
      return;
    }

    const updateTimer = () => {
      const totalSeconds = timeLimitMinutes * 60;
      const elapsedMs = Date.now() - new Date(startedAt).getTime();
      const elapsedSeconds = Math.floor(elapsedMs / 1000);
      const effectiveElapsed = elapsedSeconds - (pausedSeconds || 0);
      const remaining = Math.max(0, totalSeconds - effectiveElapsed);
      setTimeRemaining(remaining);

      // Auto-trigger time expiration when time reaches 0
      if (remaining === 0 && !hasTriggeredExpiry && onTimeExpired) {
        setHasTriggeredExpiry(true);
        onTimeExpired();
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [timeLimitMinutes, startedAt, pausedSeconds, status, hasTriggeredExpiry, onTimeExpired]);

  const formatTime = (seconds) => {
    if (seconds === null) return '--:--:--';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (status === 'paused') return 'text-warning';
    if (timeRemaining === null) return 'text-content-secondary';
    if (timeRemaining <= 0) return 'text-error';
    if (timeRemaining < 300) return 'text-error animate-pulse'; // < 5 min
    if (timeRemaining < 600) return 'text-error'; // < 10 min
    if (timeRemaining < 1800) return 'text-warning'; // < 30 min
    return 'text-success';
  };

  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  return (
    <div className="bg-surface border-b border-border px-4 py-2 flex items-center justify-between">
      {/* Timer */}
      <div className="flex items-center space-x-4">
        {timeLimitMinutes ? (
          <div className={`flex items-center space-x-2 ${getTimeColor()}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-mono text-lg font-semibold">
              {status === 'paused' ? 'PAUSAT' : formatTime(timeRemaining)}
            </span>
          </div>
        ) : (
          <span className="text-content-muted text-sm">Sense límit de temps</span>
        )}
      </div>

      {/* Actions */}
      {!isReadOnly && (
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowAbandonModal(true)}
            className="px-3 py-1.5 text-sm text-content-secondary hover:text-content border border-border-strong rounded hover:bg-surface-subtle"
          >
            Abandonar
          </button>
          <button
            onClick={() => setShowSubmitModal(true)}
            className="px-3 py-1.5 text-sm bg-accent text-white rounded hover:bg-accent-hover flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Enviar Exercici
          </button>
        </div>
      )}

      {/* Abandon Modal */}
      {showAbandonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-content flex items-center">
                <svg className="w-5 h-5 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Abandonar Exercici
              </h3>
              <button
                onClick={() => setShowAbandonModal(false)}
                className="p-1 text-content-disabled hover:text-content-secondary"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <p className="text-content-secondary mb-4">
                Estàs segur que vols abandonar l'exercici?
              </p>
              <div className="bg-warning-subtle border border-warning/30 rounded p-3 text-sm text-warning-text">
                <p className="font-semibold mb-2">ATENCIÓ:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>L'exercici s'enviarà automàticament amb l'estat actual</li>
                  {isTeamBased && <li>Afectarà a TOT L'EQUIP</li>}
                  <li>No podreu tornar a editar l'exercici</li>
                  <li>L'exercici quedarà en mode només lectura</li>
                </ul>
              </div>
              <p className="text-sm text-content-muted mt-4">
                Aquesta acció NO es pot desfer.
              </p>
            </div>
            <div className="p-4 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => setShowAbandonModal(false)}
                className="px-4 py-2 text-sm text-content-secondary border border-border-strong rounded hover:bg-surface-subtle"
              >
                Cancel·lar
              </button>
              <button
                onClick={() => {
                  setShowAbandonModal(false);
                  onAbandon();
                }}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Sí, vull abandonar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-content flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Enviar Exercici
              </h3>
              <button
                onClick={() => setShowSubmitModal(false)}
                className="p-1 text-content-disabled hover:text-content-secondary"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <p className="text-content-secondary mb-4">
                Estàs segur que vols enviar l'exercici?
              </p>
              <div className="bg-warning-subtle border border-warning/30 rounded p-3 text-sm text-warning-text">
                <p className="font-semibold mb-2">ATENCIÓ:</p>
                <ul className="list-disc list-inside space-y-1">
                  {isTeamBased && <li>Afectarà a TOT L'EQUIP</li>}
                  <li>No podreu tornar a editar l'exercici</li>
                </ul>
              </div>
            </div>
            <div className="p-4 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => setShowSubmitModal(false)}
                className="px-4 py-2 text-sm text-content-secondary border border-border-strong rounded hover:bg-surface-subtle"
              >
                Cancel·lar
              </button>
              <button
                onClick={() => {
                  setShowSubmitModal(false);
                  onSubmit();
                }}
                className="px-4 py-2 text-sm bg-accent text-white rounded hover:bg-accent-hover flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Enviar Exercici
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimerBar;
